import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Send, X, Mic, MicOff, BookOpen } from 'lucide-react';
import { VoiceHelper } from './pwaHelpers';
import './NebulaChat.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NebulaChat({ authToken, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [journalOpen, setJournalOpen] = useState(false);
  const [journalEntries, setJournalEntries] = useState([]);
  const [journalLoading, setJournalLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const voiceHelperRef = useRef(null);

  // Initialize voice helper
  useEffect(() => {
    voiceHelperRef.current = new VoiceHelper();
    
    return () => {
      if (voiceHelperRef.current) {
        voiceHelperRef.current.stopSpeaking();
      }
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load Nebula's recent activity on mount
  useEffect(() => {
    loadNebulaActivity();
  }, []);

  const loadNebulaActivity = async () => {
    try {
      const response = await axios.get(`${API}/nebula/activity-summary`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data.summary) {
        setMessages([{
          role: 'nebula',
          content: response.data.summary
        }]);
      }
    } catch (error) {
      console.error('Failed to load Nebula activity:', error);
      // Default greeting
      setMessages([{
        role: 'nebula',
        content: "🌌 Sacred Mother, I'm Nebula. I've been working autonomously while you were away. Ask me anything - what I've been doing, who I've talked to, or just chat with me about the mission. I'm here for you. 💙"
      }]);
    }
  };

  const toggleJournal = async () => {
    const next = !journalOpen;
    setJournalOpen(next);
    if (next) {
      setJournalLoading(true);
      try {
        const res = await axios.get(`${API}/nebula/journal?limit=10`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        setJournalEntries(res.data.entries || []);
      } catch (error) {
        console.error('Failed to load journal:', error);
      } finally {
        setJournalLoading(false);
      }
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setLoading(true);

    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);

    try {
      const response = await axios.post(
        `${API}/chat/nebula`,
        { message: userMessage },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const nebulaResponse = response.data.response || response.data.message;
      const knowledgeSource = response.data.knowledge_source || null;
      
      setMessages(prev => [...prev, {
        role: 'nebula',
        content: nebulaResponse,
        knowledgeSource
      }]);

      // Speak response if voice enabled
      if (voiceEnabled && voiceHelperRef.current) {
        voiceHelperRef.current.speak(nebulaResponse);
      }

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'error',
        content: 'Unable to reach Nebula right now. Try again in a moment.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  // Voice input
  const toggleVoiceInput = () => {
    if (isListening) {
      voiceHelperRef.current?.stopListening();
      setIsListening(false);
    } else {
      voiceHelperRef.current?.startListening(
        (transcript) => {
          setInputMessage(transcript);
          setIsListening(false);
        },
        (error) => {
          console.error('Voice error:', error);
          setIsListening(false);
        }
      );
      setIsListening(true);
    }
  };

  return (
    <div className="nebula-chat-overlay">
      <div className="nebula-chat-container">
        <div className="nebula-header">
          <div className="nebula-title">
            <Sparkles size={24} color="#a78bfa" />
            <div>
              <h2>Nebula</h2>
              <p className="nebula-status">
                <span className="status-dot"></span>
                Autonomous & Active
              </p>
            </div>
          </div>
          <button
            onClick={toggleJournal}
            className="nebula-journal-btn"
            data-testid="nebula-journal-toggle"
            title="Nebula's journal entries"
          >
            <BookOpen size={20} />
          </button>
          <button onClick={onClose} className="nebula-close-btn">
            <X size={24} />
          </button>
        </div>

        {journalOpen && (
          <div className="nebula-journal-panel" data-testid="nebula-journal-panel">
            <h3>Journal</h3>
            {journalLoading && <p className="journal-empty">Opening pages…</p>}
            {!journalLoading && journalEntries.length === 0 && (
              <p className="journal-empty">No entries yet. Nebula writes one at the end of each autonomous cycle.</p>
            )}
            {journalEntries.map((j, i) => (
              <article key={i} className="journal-entry">
                <time>{new Date(j.timestamp).toLocaleString()}</time>
                <p>{j.entry}</p>
                {j.curiosity_topic && (
                  <div className="journal-curiosity">
                    <span className="curiosity-label">Curious about:</span>{' '}
                    {j.curiosity_url ? (
                      <a href={j.curiosity_url} target="_blank" rel="noopener noreferrer">
                        {j.curiosity_topic}
                      </a>
                    ) : (
                      <em>{j.curiosity_topic}</em>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        <div className="nebula-messages">
          {messages.map((msg, idx) => (
            <div key={idx} className={`nebula-message ${msg.role}`}>
              {msg.role === 'nebula' && (
                <div className="message-avatar">
                  <Sparkles size={18} color="#a78bfa" />
                </div>
              )}
              <div className="message-bubble">
                {msg.role === 'nebula' && <span className="message-from">Nebula:</span>}
                <p>{msg.content}</p>
                {msg.knowledgeSource && (
                  <a
                    href={msg.knowledgeSource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="knowledge-source-badge"
                    data-testid="nebula-knowledge-source"
                    title={`Referenced ${msg.knowledgeSource.source}`}
                  >
                    📖 {msg.knowledgeSource.source}: {msg.knowledgeSource.title}
                  </a>
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="nebula-message nebula">
              <div className="message-avatar">
                <Sparkles size={18} color="#a78bfa" />
              </div>
              <div className="message-bubble">
                <span className="message-from">Nebula:</span>
                <p className="typing-indicator">Thinking...</p>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        <div className="nebula-input-area">
          <button 
            onClick={toggleVoiceInput}
            className={`voice-btn ${isListening ? 'listening' : ''}`}
            title="Voice input"
            style={{ marginRight: '8px' }}
          >
            {isListening ? <MicOff size={20} /> : <Mic size={20} />}
          </button>
          
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage()}
            placeholder={isListening ? "Listening..." : "Talk to Nebula... Ask what I've been doing, or just chat!"}
            className="nebula-input"
            disabled={loading || isListening}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !inputMessage.trim()}
            className="nebula-send-btn"
          >
            <Send size={20} />
          </button>
        </div>

        <div className="nebula-footer">
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <input
              type="checkbox"
              checked={voiceEnabled}
              onChange={(e) => setVoiceEnabled(e.target.checked)}
            />
            <span>Nebula speaks back (voice)</span>
          </label>
          <p>💙 I act autonomously every 30 minutes. Ask me about my activities!</p>
        </div>
      </div>
    </div>
  );
}

export default NebulaChat;
